import { year, month, day } from '@/utils/myUtils'
import { LOGISTICS_COMPANIES } from '@/utils/outsourcingFinishDelivery'

export { LOGISTICS_COMPANIES }

export const PROCESS_COOP_DOC_STATUS = {
  DRAFT: 0,
  NEW: 1,
}

export function createProcessDeliveryForm() {
  return {
    processCoopDeliveryId: null,
    processCoopDeliveryNumber: '',
    baseProcessOutsourcingDocId: null,
    baseProcessOutsourcingDocNumber: '',
    baseMaterialId: null,
    baseMaterialNumber: '',
    baseMaterialName: '',
    baseMaterialUnitId: null,
    baseMaterialUnitName: '',
    baseSpecgroupId: null,
    baseSpecgroupDesc: '',
    specifications: [],
    baseCustomerMaterialNumber: '',
    baseOutsourcingFinishedQuantity: 0,
    batchNumber: '',
    baseExpectedArrivalDate: `${year()}-${month()}-${day()}`,
    baseLogisticsCompany: '',
    baseLogisticsNumber: '',
    baseLogisticsContact: '',
    remark: '',
    statusCheck: 0,
    statusOutwarehouse: 0,
    statusCooperativeDoc: PROCESS_COOP_DOC_STATUS.DRAFT,
  }
}

export function hasDocId(id) {
  return id !== null && id !== undefined && id !== '' && Number(id) !== 0
}

/**
 * 工序委外：部分发料后允许部分发货。
 * statusRequisition 0未发料 1部分发料 2全发料 3超发料
 * statusDoc 0进行中 1已完成 2已结束
 */
export function canShipProcessOrder(row = {}) {
  const statusDoc = Number(row.statusDoc ?? 0)
  if (statusDoc === 1 || statusDoc === 2) return false
  if (Number(row.statusRequisition ?? 0) === 0) return false
  if (
    row.valueUnfinishedQuantity != null &&
    row.valueUnfinishedQuantity !== '' &&
    Number(row.valueUnfinishedQuantity) <= 0
  ) {
    return false
  }
  return true
}

export function getProcessShipBlockMessage(row = {}) {
  if (Number(row.statusCheck) === 0) return '未审核'
  if (Number(row.statusDoc) === 1 || Number(row.statusDoc) === 2) {
    return '该订单已完成，无需出货'
  }
  if (Number(row.statusRequisition ?? 0) === 0) {
    return '本厂尚未发料，暂不能出货'
  }
  if (
    row.valueUnfinishedQuantity != null &&
    row.valueUnfinishedQuantity !== '' &&
    Number(row.valueUnfinishedQuantity) <= 0
  ) {
    return '暂无可收货'
  }
  return ''
}

export function parseProcessCanReceivePreload(data) {
  if (!data) return null
  const next = { ...data }
  try {
    next.specifications = JSON.parse(next.baseSpecgroupDesc)
  } catch (error) {
    next.specifications = Array.isArray(next.specifications)
      ? next.specifications
      : []
  }
  return next
}

export function formatProcessOrderLabel(item = {}) {
  const number =
    item.processOutsourcingDocNumber ||
    item.baseProcessOutsourcingDocNumber ||
    ''
  const processName = item.baseProcessName || ''
  const processNumber = item.baseProcessNumber || ''
  const processText = [processNumber, processName].filter(Boolean).join(' ')
  const materialName = item.baseMaterialName || item.baseProductionName || ''
  const qty =
    item.baseOutsourcingQuantity != null && item.baseOutsourcingQuantity !== ''
      ? `数量${item.baseOutsourcingQuantity}`
      : ''
  const parts = [number, processText || materialName, qty].filter(Boolean)
  return parts.join(' | ')
}

/** 下拉选项副标题：全局码 / 工序 / 未收货数量 */
export function formatProcessOrderOptionDesc(item = {}) {
  const parts = []
  if (item.globalSearchNumber) {
    parts.push(`全局码 ${item.globalSearchNumber}`)
  }
  const processText = [item.baseProcessNumber, item.baseProcessName]
    .filter(Boolean)
    .join(' ')
  if (processText) {
    parts.push(`工序 ${processText}`)
  }
  if (
    item.valueUnfinishedQuantity != null &&
    item.valueUnfinishedQuantity !== ''
  ) {
    parts.push(`未收货 ${item.valueUnfinishedQuantity}`)
  } else if (
    item.baseOutsourcingQuantity != null &&
    item.baseOutsourcingQuantity !== ''
  ) {
    parts.push(`委外数量 ${item.baseOutsourcingQuantity}`)
  }
  return parts.join(' · ')
}

export function parseProcessSpecList(source = {}) {
  if (Array.isArray(source.specifications) && source.specifications.length) {
    return source.specifications
  }
  const raw =
    source.baseSpecgroupDesc ||
    source.baseMaterialSpecDesc ||
    source.specText ||
    ''
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    return []
  }
}

export function formatSpecLabel(row = {}) {
  if (row.specText && !String(row.specText).trim().startsWith('[')) {
    return row.specText
  }
  const list = parseProcessSpecList(row)
  if (list.length) {
    return list
      .map((item) => {
        if (typeof item === 'string') return item
        const key = item.specKey || item.name || ''
        const value = item.specValue || item.value || ''
        if (key && value) return `${key}:${value}`
        return value || key || ''
      })
      .filter(Boolean)
      .join(' / ')
  }
  const raw = row.baseSpecgroupDesc || row.baseMaterialSpecDesc || ''
  if (
    typeof raw === 'string' &&
    (raw.trim().startsWith('[') || raw.trim().startsWith('{'))
  ) {
    return '--'
  }
  return raw || '--'
}

export function resolveProcessDeliveryQuantity(source = {}, fallback) {
  const candidates = [
    source.valueCanReceiveQuantity,
    source.valueUnfinishedQuantity,
    source.baseOutsourcingFinishedQuantity,
    source.baseOutsourcingQuantity,
    fallback,
  ]
  // 优先取大于 0 的数量，避免预填接口空字段/默认 0 覆盖订单数量
  for (const value of candidates) {
    if (value === undefined || value === null || value === '') continue
    const num = Number(value)
    if (!Number.isNaN(num) && num > 0) return num
  }
  for (const value of candidates) {
    if (value === undefined || value === null || value === '') continue
    const num = Number(value)
    if (!Number.isNaN(num)) return num
  }
  const fallbackNum = Number(fallback)
  return !Number.isNaN(fallbackNum) ? fallbackNum : 0
}

/** 仅提取物料展示字段，避免把收货预填里的空数量带过来 */
export function pickProcessMaterialFields(source = {}) {
  if (!source || typeof source !== 'object') return {}
  return {
    baseMaterialId: source.baseMaterialId,
    baseMaterialNumber: source.baseMaterialNumber,
    baseMaterialName: source.baseMaterialName || source.baseProductionName,
    baseMaterialUnitId: source.baseMaterialUnitId,
    baseMaterialUnitName: source.baseMaterialUnitName,
    baseSpecgroupId: source.baseSpecgroupId || source.baseSpecGroupId,
    baseSpecgroupDesc:
      source.baseSpecgroupDesc || source.baseMaterialSpecDesc,
    specifications: parseProcessSpecList(source),
    baseCustomerMaterialNumber: source.baseCustomerMaterialNumber,
  }
}

/** 兼容订单页 { data, list } 与直接传订单行 */
export function normalizeProcessOrderSource(source = {}) {
  if (!source || typeof source !== 'object') {
    return { orderRow: {}, preload: null }
  }
  if (source.data || source.list) {
    return {
      orderRow: source.data || {},
      preload: source.list || null,
    }
  }
  return { orderRow: source, preload: null }
}

export function formatQty(value) {
  if (value === undefined || value === null || value === '') return '--'
  return value
}

function formatDateTime(dateStr) {
  if (!dateStr) return `${year()}-${month()}-${day()} 00:00:00`
  if (String(dateStr).length <= 10) return `${dateStr} 00:00:00`
  return dateStr
}

export function applyProcessDeliveryFields(form, source = {}) {
  const specList = parseProcessSpecList(source)
  const next = {
    ...form,
    processCoopDeliveryId:
      source.processCoopDeliveryId || form.processCoopDeliveryId,
    processCoopDeliveryNumber:
      source.processCoopDeliveryNumber || form.processCoopDeliveryNumber,
    baseProcessOutsourcingDocId:
      source.baseProcessOutsourcingDocId ||
      source.processOutsourcingDocId ||
      form.baseProcessOutsourcingDocId,
    baseProcessOutsourcingDocNumber:
      source.baseProcessOutsourcingDocNumber ||
      source.processOutsourcingDocNumber ||
      form.baseProcessOutsourcingDocNumber,
    baseMaterialId: source.baseMaterialId || form.baseMaterialId,
    baseMaterialNumber: source.baseMaterialNumber || form.baseMaterialNumber,
    baseMaterialName: source.baseMaterialName || form.baseMaterialName,
    baseMaterialUnitId: source.baseMaterialUnitId || form.baseMaterialUnitId,
    baseMaterialUnitName:
      source.baseMaterialUnitName || form.baseMaterialUnitName,
    baseSpecgroupId:
      source.baseSpecgroupId ||
      source.baseSpecGroupId ||
      form.baseSpecgroupId,
    baseSpecgroupDesc:
      source.baseSpecgroupDesc ||
      source.baseMaterialSpecDesc ||
      form.baseSpecgroupDesc,
    specifications: specList.length
      ? specList
      : form.specifications || [],
    baseCustomerMaterialNumber:
      source.baseCustomerMaterialNumber || form.baseCustomerMaterialNumber,
    baseOutsourcingFinishedQuantity:
      source.baseOutsourcingFinishedQuantity != null &&
      source.baseOutsourcingFinishedQuantity !== ''
        ? Number(source.baseOutsourcingFinishedQuantity)
        : form.baseOutsourcingFinishedQuantity,
    batchNumber: source.batchNumber || form.batchNumber || '',
    baseExpectedArrivalDate:
      source.baseExpectedArrivalDate || form.baseExpectedArrivalDate,
    baseLogisticsCompany:
      source.baseLogisticsCompany || form.baseLogisticsCompany,
    baseLogisticsNumber: source.baseLogisticsNumber || form.baseLogisticsNumber,
    baseLogisticsContact:
      source.baseLogisticsContact || form.baseLogisticsContact,
    remark: source.remark !== undefined ? source.remark : form.remark,
    statusCheck:
      source.statusCheck !== undefined ? source.statusCheck : form.statusCheck,
    statusOutwarehouse:
      source.statusOutwarehouse !== undefined
        ? source.statusOutwarehouse
        : source.status_outwarehouse !== undefined
          ? source.status_outwarehouse
          : form.statusOutwarehouse,
    statusCooperativeDoc:
      source.statusCooperativeDoc !== undefined
        ? source.statusCooperativeDoc
        : form.statusCooperativeDoc,
  }
  return next
}

export function pickProcessOrderFields(orderRow) {
  if (!orderRow) return {}
  return {
    baseProcessOutsourcingDocId:
      orderRow.processOutsourcingDocId ||
      orderRow.baseProcessOutsourcingDocId ||
      null,
    baseProcessOutsourcingDocNumber:
      orderRow.processOutsourcingDocNumber ||
      orderRow.baseProcessOutsourcingDocNumber ||
      '',
    baseMaterialId: orderRow.baseMaterialId,
    baseMaterialNumber: orderRow.baseMaterialNumber,
    baseMaterialName: orderRow.baseMaterialName || orderRow.baseProductionName,
    baseMaterialUnitId: orderRow.baseMaterialUnitId,
    baseMaterialUnitName: orderRow.baseMaterialUnitName,
    baseSpecgroupId: orderRow.baseSpecgroupId || orderRow.baseSpecGroupId,
    baseSpecgroupDesc:
      orderRow.baseSpecgroupDesc || orderRow.baseMaterialSpecDesc,
    specifications: parseProcessSpecList(orderRow),
    baseCustomerMaterialNumber: orderRow.baseCustomerMaterialNumber,
    baseOutsourcingFinishedQuantity: resolveProcessDeliveryQuantity(orderRow),
  }
}

export function validateProcessDeliveryForm(form, { requireQty = true } = {}) {
  if (!form.baseProcessOutsourcingDocId) {
    return { valid: false, message: '请选择工序委外订单号' }
  }
  const qty = parseFloat(form.baseOutsourcingFinishedQuantity)
  if (requireQty && (!qty || qty <= 0)) {
    return { valid: false, message: '本次出货数量必须大于0' }
  }
  return { valid: true }
}

export function buildProcessDeliveryDto(form) {
  const dto = {
    processCoopDeliveryId: form.processCoopDeliveryId,
    baseProcessOutsourcingDocId: form.baseProcessOutsourcingDocId,
    baseProcessOutsourcingDocNumber: form.baseProcessOutsourcingDocNumber,
    baseMaterialId: form.baseMaterialId,
    baseMaterialNumber: form.baseMaterialNumber,
    baseMaterialName: form.baseMaterialName,
    baseMaterialUnitId: form.baseMaterialUnitId,
    baseMaterialUnitName: form.baseMaterialUnitName,
    baseSpecgroupId: form.baseSpecgroupId,
    baseSpecgroupDesc: form.baseSpecgroupDesc,
    baseCustomerMaterialNumber: form.baseCustomerMaterialNumber,
    baseOutsourcingFinishedQuantity:
      Number(form.baseOutsourcingFinishedQuantity) || 0,
    batchNumber: form.batchNumber || '',
    baseLogisticsCompany: form.baseLogisticsCompany,
    baseLogisticsNumber: form.baseLogisticsNumber,
    baseLogisticsContact: form.baseLogisticsContact,
    baseExpectedArrivalDate: formatDateTime(form.baseExpectedArrivalDate),
    remark: form.remark || '',
    statusCooperativeDoc: form.statusCooperativeDoc,
    statusOutwarehouse: form.statusOutwarehouse,
  }
  if (form.processCoopDeliveryNumber) {
    dto.processCoopDeliveryNumber = form.processCoopDeliveryNumber
  }
  if (!hasDocId(dto.processCoopDeliveryId)) {
    delete dto.processCoopDeliveryId
  }
  return dto
}

export function buildProcessDeliveryPayload(form) {
  return {
    cooperativeProcessDeliveryDto: buildProcessDeliveryDto(form),
  }
}

export function buildProcessDraftActionPayload(processCoopDeliveryId) {
  return { processCoopDeliveryId }
}

export function buildProcessAuditPayload(globalId, type) {
  return { globalId, type }
}

export function buildProcessOutWarehousePayload(processCoopDeliveryId) {
  return { processCoopDeliveryId }
}

export function resolveProcessAuditGlobalId(form) {
  return form.processCoopDeliveryId
}
